/**
 * DM2 burst control — one per-tab budget for fresh doc-match Gemini calls.
 *
 * In the browser every fresh DM2 score (`scoreDocAgainstChecklistItem` on a
 * cache miss) is a `/api/gemini-proxy` request, and the proxy meters requests
 * per client IP in the shared `generalRateLimit` bucket: 100 per 60 s fixed
 * window (`src/server/ipRateLimit.ts`), answered with `429` +
 * `Retry-After: 60` once spent. The ranked matcher fires one call per
 * document per checklist item at once (`Promise.all`), so the first open of a
 * site with many unscored pairs spent the bucket in a second; the
 * 2026-09-07/08 census traced 137 "Rate limit exceeded" rows to that burst.
 *
 * Every fresh call takes a slot here first. Three limits, all on the
 * fresh-call path only — a Firestore cache hit never waits:
 *
 *   1. at most `maxInFlight` (4) requests in flight;
 *   2. at most `maxPerWindow` (60) request starts in any `windowMs` (60 s)
 *      sliding window. Any fixed 60 s server window lies inside some sliding
 *      one, so this bounds the server's count too, and leaves 40 of the 100
 *      for the tab's other calls on the same bucket (other Gemini calls and
 *      every other `generalRateLimit` route). Concurrency alone cannot do
 *      this: 4 in flight at ~1 s per call is ~240 requests a minute;
 *   3. no start before the proxy's `Retry-After` has passed once any proxied
 *      call saw a 429 (`lib/geminiProxyRateLimit`, fed by the fetch patch and
 *      by `noteScoringRateLimited`).
 *
 * Waiters are served first in, first out. A waiter whose `signal` aborts
 * leaves the queue with the signal's reason; a slot already granted is kept
 * until its call settles, so an in-flight request always counts.
 *
 * A retry inside `runGeminiJSON` is a second request on the same slot:
 * `noteScoringRetryRequest` records it in the window (without waiting), so
 * the calls that follow are paced by the real request count. That can put at
 * most `maxInFlight` × 2 extra starts in one window (3 attempts per slot) —
 * within the 40 of headroom.
 *
 * Browser only. On a server runtime (`typeof window === 'undefined'`) the
 * scorer reaches Vertex directly — no proxy, no IP bucket — and this module
 * is a pass-through, so worker behaviour is unchanged.
 */
import { abortReason, throwIfAborted } from '../../lib/abortSignal';
import { geminiProxyBlockedUntil, noteGeminiProxyRateLimited } from '../../lib/geminiProxyRateLimit';
import { classifyGenAIError } from '../genaiRetry';

/** Fresh doc-match requests in flight at once, per tab. */
export const DOC_MATCH_MAX_IN_FLIGHT = 4;
/** Fresh doc-match request starts allowed in any sliding window, per tab. */
export const DOC_MATCH_MAX_PER_WINDOW = 60;
/** The sliding window — the proxy bucket's 60 s (`IP_RATE_LIMIT_WINDOW_MS`). */
export const DOC_MATCH_WINDOW_MS = 60_000;

export interface ScoringThrottleLimits {
  maxInFlight: number;
  maxPerWindow: number;
  windowMs: number;
}

const DEFAULT_LIMITS: ScoringThrottleLimits = {
  maxInFlight: DOC_MATCH_MAX_IN_FLIGHT,
  maxPerWindow: DOC_MATCH_MAX_PER_WINDOW,
  windowMs: DOC_MATCH_WINDOW_MS,
};

/** Calls from the browser go through the proxy; server calls do not. */
const defaultEnabled = (): boolean => typeof window !== 'undefined';

interface Waiter {
  resolve: (release: () => void) => void;
  reject: (error: Error) => void;
  signal?: AbortSignal;
  onAbort?: () => void;
}

let enabled = defaultEnabled();
let limits: ScoringThrottleLimits = { ...DEFAULT_LIMITS };
let inFlight = 0;
/** Request start times (epoch ms) still inside the window, ascending. */
let starts: number[] = [];
const waiters: Waiter[] = [];
let wakeTimer: ReturnType<typeof setTimeout> | undefined;
let wakeAt = Number.POSITIVE_INFINITY;
let started = 0;
let rateLimited = 0;

const NOOP_RELEASE = (): void => {};

/** True when fresh calls are throttled (a browser runtime, i.e. the proxy path). */
export function isScoringThrottleEnabled(): boolean {
  return enabled;
}

/** Drop starts that have left the window (a start `windowMs` old still counts). */
function prune(now: number): void {
  let drop = 0;
  while (drop < starts.length && now - starts[drop]! > limits.windowMs) drop += 1;
  if (drop > 0) starts = starts.slice(drop);
}

/** Earliest epoch ms at which a new request may start (ignoring the slot count). */
function nextStartAt(now: number): number {
  let at = Math.max(now, geminiProxyBlockedUntil());
  if (starts.length >= limits.maxPerWindow) {
    // The start that must leave the window for the count to drop below the cap.
    at = Math.max(at, starts[starts.length - limits.maxPerWindow]! + limits.windowMs + 1);
  }
  return at;
}

function scheduleWake(at: number, now: number): void {
  if (wakeTimer !== undefined && wakeAt <= at) return;
  if (wakeTimer !== undefined) clearTimeout(wakeTimer);
  wakeAt = at;
  wakeTimer = setTimeout(() => {
    wakeTimer = undefined;
    wakeAt = Number.POSITIVE_INFINITY;
    pump();
  }, Math.max(0, at - now));
}

function makeRelease(): () => void {
  let released = false;
  return () => {
    if (released) return;
    released = true;
    inFlight -= 1;
    pump();
  };
}

/** Grant slots to waiters, in order, while every limit allows. */
function pump(): void {
  const now = Date.now();
  prune(now);
  while (waiters.length > 0 && inFlight < limits.maxInFlight) {
    const readyAt = nextStartAt(now);
    if (readyAt > now) {
      scheduleWake(readyAt, now);
      return;
    }
    const waiter = waiters.shift()!;
    if (waiter.signal && waiter.onAbort) waiter.signal.removeEventListener('abort', waiter.onAbort);
    inFlight += 1;
    starts.push(now);
    started += 1;
    waiter.resolve(makeRelease());
  }
}

/**
 * Wait for a slot for one fresh request and return its release. Call the
 * release exactly once when the request has settled (extra calls are
 * ignored). Rejects only when `signal` aborts before the slot is granted.
 * A pass-through on a server runtime.
 */
export function acquireScoringSlot(signal?: AbortSignal): Promise<() => void> {
  try {
    throwIfAborted(signal);
  } catch (e) {
    return Promise.reject(e);
  }
  if (!enabled) return Promise.resolve(NOOP_RELEASE);
  return new Promise<() => void>((resolve, reject) => {
    const waiter: Waiter = { resolve, reject, signal };
    if (signal) {
      waiter.onAbort = () => {
        const index = waiters.indexOf(waiter);
        if (index < 0) return;
        waiters.splice(index, 1);
        reject(abortReason(signal));
      };
      signal.addEventListener('abort', waiter.onAbort, { once: true });
    }
    waiters.push(waiter);
    pump();
  });
}

/**
 * Count a retry that `runGeminiJSON` fires on a slot it already holds: one
 * more request start in the window. It does not wait — the retry is already
 * part of a granted call — but the calls after it are paced by it.
 */
export function noteScoringRetryRequest(): void {
  if (!enabled) return;
  const now = Date.now();
  prune(now);
  starts.push(now);
}

/**
 * True for an error that means "rate limited — back off": the proxy's own
 * 429 (`ApiError.status` 429, body "Rate limit exceeded") or an upstream
 * 429 / RESOURCE_EXHAUSTED / per-minute quota passed through it. A terminal
 * spend cap or an auth / invalid-request error is not.
 */
export function isRateLimitedError(error: unknown): boolean {
  const classification = classifyGenAIError(error);
  if (classification.startsWith('fatal')) return false;
  if ((error as { status?: unknown } | null | undefined)?.status === 429) return true;
  const message = error instanceof Error ? error.message : String(error ?? '');
  if (/rate limit exceeded/i.test(message)) return true;
  return classification === 'transient-429';
}

/**
 * Report a rate-limited scoring call. Opens the proxy's back-off window — the
 * default 60 s, unless the fetch patch already recorded the response's own
 * `Retry-After` — so no new request starts before it passes. Returns the
 * epoch ms the window ends.
 */
export function noteScoringRateLimited(): number {
  rateLimited += 1;
  const until = noteGeminiProxyRateLimited(undefined, Date.now());
  pump();
  return until;
}

export interface ScoringThrottleTelemetry {
  enabled: boolean;
  /** Requests holding a slot now. */
  inFlight: number;
  /** Calls waiting for a slot now. */
  queued: number;
  /** Slots granted since load (retries on a slot not included). */
  started: number;
  /** Rate-limited scoring calls reported since load. */
  rateLimited: number;
  /** Epoch ms before which no new request starts (0 = never blocked). */
  blockedUntil: number;
}

/** In-tab counters for tests and a dev-tools check after a cold site open. */
export function getScoringThrottleTelemetry(): ScoringThrottleTelemetry {
  return {
    enabled,
    inFlight,
    queued: waiters.length,
    started,
    rateLimited,
    blockedUntil: geminiProxyBlockedUntil(),
  };
}

/** Test hook — enable/disable the throttle and override its limits. */
export function configureScoringThrottleForTests(
  opts: Partial<ScoringThrottleLimits> & { enabled?: boolean },
): void {
  const { enabled: on, ...rest } = opts;
  if (on !== undefined) enabled = on;
  limits = { ...limits, ...rest };
}

/** Test hook — drop all state and restore the defaults. Queued waiters are abandoned. */
export function resetScoringThrottleForTests(): void {
  if (wakeTimer !== undefined) clearTimeout(wakeTimer);
  wakeTimer = undefined;
  wakeAt = Number.POSITIVE_INFINITY;
  for (const waiter of waiters) {
    if (waiter.signal && waiter.onAbort) waiter.signal.removeEventListener('abort', waiter.onAbort);
  }
  waiters.length = 0;
  enabled = defaultEnabled();
  limits = { ...DEFAULT_LIMITS };
  inFlight = 0;
  starts = [];
  started = 0;
  rateLimited = 0;
}
